/**
 * Запись встречи — целиком в браузере.
 *
 * ── Главное ограничение, из которого всё вытекает ───────────────────
 *
 * Запись не должна попадать на сервер. Поэтому здесь нет ни загрузки, ни
 * временных файлов: кадры собираются в canvas на самой странице, звук
 * сводится через WebAudio, `MediaRecorder` складывает куски в память, а
 * по остановке файл сразу отдаётся браузеру на скачивание. Если человек
 * скачивание отменил — куски отпускаются и запись исчезает навсегда,
 * восстановить её неоткуда.
 *
 * Картинка берётся с тех же `<video>`, что видны на экране. Это не только
 * проще, чем собирать раскладку заново, но и честнее: в файл попадает
 * ровно то, что человек видел, включая демонстрацию экрана и развёрнутого
 * во весь экран собеседника.
 */

const FPS = 25;
const AUDIO_BITS = 128_000;

/**
 * Размер и битрейт кадра записи.
 *
 * ── Почему два профиля, а не один ───────────────────────────────────
 *
 * Разговор и презентация нагружают запись по-разному. В разговоре кадр —
 * это лица: деталей мало, движения много, 720p при 2.5 Мбит/с даёт час
 * записи примерно на гигабайт и выглядит достойно.
 *
 * В режиме презентации в кадре документ или слайд с мелким шрифтом.
 * Там всё наоборот: движения почти нет, а деталей много, и 720p
 * превращает восьмой кегль в серую кашу. Поэтому кадр 1080p и битрейт
 * выше — при статичной картинке кодек всё равно тратит меньше, чем
 * на говорящие головы, так что файл вырастает не втрое, а примерно
 * в полтора раза.
 */
export type RecordingQuality = 'meeting' | 'presentation';

const PROFILES: Record<RecordingQuality, { width: number; height: number; bits: number }> = {
  meeting: { width: 1280, height: 720, bits: 2_500_000 },
  presentation: { width: 1920, height: 1080, bits: 4_000_000 },
};

/** Форматы по убыванию предпочтения: VP9 экономнее, MP4 нужен Safari. */
const FORMATS = [
  'video/webm;codecs=vp9,opus',
  'video/webm;codecs=vp8,opus',
  'video/webm',
  'video/mp4',
];

export function recordingSupported(): boolean {
  return (
    typeof MediaRecorder !== 'undefined' &&
    typeof HTMLCanvasElement !== 'undefined' &&
    typeof HTMLCanvasElement.prototype.captureStream === 'function' &&
    FORMATS.some((f) => MediaRecorder.isTypeSupported(f))
  );
}

function pickFormat(): string | undefined {
  return FORMATS.find((f) => MediaRecorder.isTypeSupported(f));
}

/** Имя файла: по нему запись потом находят среди загрузок. */
export function recordingFileName(title: string, mime: string): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  const stamp = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}-${pad(now.getMinutes())}`;
  // В именах файлов эти символы запрещены или мешают — заменяем на дефис.
  const safe = (title || 'Встреча').replace(/[\\/:*?"<>|]+/g, '-').trim().slice(0, 60);
  const ext = mime.includes('mp4') ? 'mp4' : 'webm';
  return `GTO CONNECT — ${safe} — ${stamp}.${ext}`;
}

interface Sources {
  /** Профиль качества: зависит от того, что в кадре — лица или документ. */
  quality?: RecordingQuality;
  /** Откуда брать кадры: контейнер с плитками участников. */
  stage: HTMLElement | null;
  /** Все звуковые дорожки встречи, включая свой микрофон. */
  audioTracks: () => MediaStreamTrack[];
}

export class MeetingRecorder {
  private canvas = document.createElement('canvas');
  private ctx = this.canvas.getContext('2d');
  private profile: { width: number; height: number; bits: number };
  private frame = 0;
  private recorder: MediaRecorder | null = null;
  private chunks: Blob[] = [];
  private audio: AudioContext | null = null;
  private mixed: MediaStreamAudioDestinationNode | null = null;
  private silence: ConstantSourceNode | null = null;
  private connected = new Map<string, MediaStreamAudioSourceNode>();
  private rescan = 0;
  private mime = '';

  constructor(private sources: Sources) {
    this.profile = PROFILES[sources.quality ?? 'meeting'];
    this.canvas.width = this.profile.width;
    this.canvas.height = this.profile.height;
  }

  start(): void {
    const mime = pickFormat();
    if (!mime || !this.ctx) throw new Error('Браузер не умеет записывать видео');
    this.mime = mime;

    this.audio = new AudioContext();
    this.mixed = this.audio.createMediaStreamDestination();

    /*
     * Постоянная тишина в шину — иначе записи не будет вовсе.
     *
     * Звуковая шина, к которой ничего не подключено, отдаёт дорожку, по
     * которой не приходит ни одного отсчёта. `MediaRecorder` ждёт звук,
     * чтобы переложить его вместе с кадрами, не дожидается — и не отдаёт
     * НИЧЕГО: ни звука, ни картинки. Файл получается пустым, а человек
     * узнаёт об этом только когда остановит запись и ничего не скачается.
     *
     * Случай не выдуманный: так происходит всегда, когда запись включили
     * раньше, чем кто-нибудь включил микрофон, — то есть в самом начале
     * встречи, когда её включают чаще всего.
     *
     * Источник постоянного значения с нулём на выходе даёт ровный поток
     * нулевых отсчётов: настоящая тишина, слышимую часть записи не
     * меняет, но шина больше не молчит.
     */
    this.silence = this.audio.createConstantSource();
    this.silence.offset.value = 0;
    this.silence.connect(this.mixed);
    this.silence.start();

    this.syncAudio();
    // Участники приходят и уходят по ходу встречи — подмешиваем их звук
    // на лету, иначе в записи останутся только те, кто был в начале.
    this.rescan = window.setInterval(() => this.syncAudio(), 2000);

    const stream = this.canvas.captureStream(FPS);
    this.mixed.stream.getAudioTracks().forEach((t) => stream.addTrack(t));

    this.chunks = [];
    this.recorder = new MediaRecorder(stream, {
      mimeType: mime,
      videoBitsPerSecond: this.profile.bits,
      audioBitsPerSecond: AUDIO_BITS,
    });
    this.recorder.ondataavailable = (e) => {
      if (e.data.size > 0) this.chunks.push(e.data);
    };
    // Куски раз в секунду: при аварийном закрытии вкладки теряется
    // максимум последняя секунда, а не вся запись.
    this.recorder.start(1000);

    /*
     * Кадры рисуются по таймеру, а не через requestAnimationFrame.
     *
     * rAF замирает, как только вкладка уходит в фон, — а во время встречи
     * это обычное дело: человек переключается в почту или в документ.
     * С rAF запись в этот момент переставала получать кадры и на выходе
     * не оставалось вообще ничего. Таймер в фоне тоже притормаживают, но
     * он продолжает идти, и запись просто становится реже по кадрам
     * вместо того, чтобы оборваться.
     */
    this.draw();
    this.frame = window.setInterval(this.draw, Math.round(1000 / FPS));
  }

  /**
   * Останавливает запись и отдаёт файл.
   *
   * Возвращает `false`, если писать было нечего. Данные в любом случае
   * отпускаются: на сервере их нет и не будет, а в памяти держать нечего.
   */
  stop(title: string): Promise<boolean> {
    return new Promise((resolve) => {
      const recorder = this.recorder;
      if (!recorder || recorder.state === 'inactive') {
        this.cleanup();
        resolve(false);
        return;
      }

      recorder.onstop = () => {
        const chunks = this.chunks;
        this.chunks = [];
        this.cleanup();

        if (chunks.length === 0) {
          resolve(false);
          return;
        }

        const blob = new Blob(chunks, { type: this.mime });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = recordingFileName(title, this.mime);
        document.body.appendChild(link);
        link.click();
        link.remove();

        // Ссылку отпускаем сразу после того, как браузер её забрал.
        // Дальше запись живёт только в загрузках у человека — у нас её
        // не остаётся ни в памяти, ни где-либо ещё.
        setTimeout(() => URL.revokeObjectURL(url), 10_000);
        resolve(true);
      };

      recorder.stop();
    });
  }

  /** Аварийное завершение: всё отпускаем, ничего не сохраняем. */
  discard(): void {
    try {
      if (this.recorder && this.recorder.state !== 'inactive') {
        this.recorder.onstop = null;
        this.recorder.stop();
      }
    } catch {
      // Уже остановлен — ничего страшного.
    }
    this.chunks = [];
    this.cleanup();
  }

  private cleanup(): void {
    try {
      this.silence?.stop();
    } catch {
      // Уже остановлен — ничего страшного.
    }
    this.silence?.disconnect();
    this.silence = null;

    if (this.frame) clearInterval(this.frame);
    this.frame = 0;
    if (this.rescan) clearInterval(this.rescan);
    this.rescan = 0;
    this.connected.forEach((node) => node.disconnect());
    this.connected.clear();
    this.audio?.close().catch(() => {});
    this.audio = null;
    this.mixed = null;
    this.recorder = null;
  }

  /** Подмешивает звук всех участников, следя за приходом и уходом. */
  private syncAudio(): void {
    const audio = this.audio;
    const mixed = this.mixed;
    if (!audio || !mixed) return;

    const tracks = this.sources.audioTracks();
    const live = new Set(tracks.map((t) => t.id));

    for (const [id, node] of this.connected) {
      if (!live.has(id)) {
        node.disconnect();
        this.connected.delete(id);
      }
    }

    for (const track of tracks) {
      if (this.connected.has(track.id)) continue;
      try {
        const node = audio.createMediaStreamSource(new MediaStream([track]));
        node.connect(mixed);
        this.connected.set(track.id, node);
      } catch {
        // Дорожка уже закрыта — просто пропускаем.
      }
    }
  }

  /**
   * Перерисовывает кадр записи по текущей раскладке на экране.
   *
   * Позиции берутся у настоящих плиток и пересчитываются в координаты
   * холста, поэтому запись повторяет то, что видит человек, — включая
   * демонстрацию экрана и развёрнутого участника.
   */
  private draw = (): void => {
    const ctx = this.ctx;
    const stage = this.sources.stage;
    if (!ctx) return;

    const { width: W, height: H } = this.profile;

    ctx.fillStyle = '#111115';
    ctx.fillRect(0, 0, W, H);
    if (!stage) return;

    const area = stage.getBoundingClientRect();
    if (area.width === 0 || area.height === 0) return;

    // Вписываем прямоугольник сцены в кадр записи целиком, сохраняя
    // пропорции: иначе на широком мониторе плитки растянулись бы.
    const scale = Math.min(W / area.width, H / area.height);
    const offsetX = (W - area.width * scale) / 2;
    const offsetY = (H - area.height * scale) / 2;

    for (const tile of Array.from(stage.querySelectorAll<HTMLElement>('.tile'))) {
      const video = tile.querySelector('video');
      const box = tile.getBoundingClientRect();

      /*
       * Прозрачность плитки переносим в запись.
       *
       * В режиме презентации окно докладчика плавно появляется и уходит
       * через `opacity`. Без этой строки в записи оно было бы всегда
       * видно во всю силу и вдобавок дёргалось бы: появлялось и
       * исчезало рывком вместо плавного перехода.
       */
      const alpha = tileOpacity(tile);
      if (alpha <= 0.01) continue;
      ctx.globalAlpha = alpha;
      const x = offsetX + (box.left - area.left) * scale;
      const y = offsetY + (box.top - area.top) * scale;
      const w = box.width * scale;
      const h = box.height * scale;

      ctx.fillStyle = '#0b0b0e';
      ctx.fillRect(x, y, w, h);

      if (video && video.videoWidth > 0 && !video.classList.contains('tile__video--hidden')) {
        // Кадр вписывается целиком — так же, как на экране (`contain`).
        const fit = Math.min(w / video.videoWidth, h / video.videoHeight);
        const vw = video.videoWidth * fit;
        const vh = video.videoHeight * fit;
        ctx.drawImage(video, x + (w - vw) / 2, y + (h - vh) / 2, vw, vh);
      } else {
        const initials = tile.querySelector('.tile__avatar')?.textContent ?? '';
        ctx.fillStyle = '#9c9cab';
        ctx.font = `600 ${Math.round(Math.min(w, h) * 0.18)}px Montserrat, sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(initials, x + w / 2, y + h / 2);
      }

      // Имя берём из атрибута, который плитка готовит специально для
      // записи: экранная подпись содержит ещё и «демонстрация экрана»,
      // а в файле это только мешает.
      const name = tile.dataset.recordName ?? tile.querySelector('.tile__name-text')?.textContent;
      if (name && h > 60) {
        const size = Math.max(11, Math.round(h * 0.055));
        ctx.font = `600 ${size}px Montserrat, sans-serif`;
        ctx.textAlign = 'left';
        ctx.textBaseline = 'bottom';
        ctx.fillStyle = 'rgba(11,11,14,0.72)';
        const pad = size * 0.5;
        const textWidth = ctx.measureText(name).width;
        ctx.fillRect(x + pad, y + h - pad - size * 1.6, textWidth + pad * 2, size * 1.6);
        ctx.fillStyle = '#ffffff';
        ctx.fillText(name, x + pad * 2, y + h - pad * 1.3);
      }

      ctx.globalAlpha = 1;
    }
  };
}

/**
 * Итоговая прозрачность плитки с учётом родителей.
 *
 * Гасится не сама плитка, а обёртка вокруг неё (`.present__pip`),
 * поэтому смотреть только на плитку недостаточно. Поднимаемся до сцены,
 * перемножая по дороге: ровно так это и считает браузер.
 */
function tileOpacity(tile: HTMLElement): number {
  let alpha = 1;
  let node: HTMLElement | null = tile;

  while (node && !node.classList.contains('room__video')) {
    const value = Number.parseFloat(getComputedStyle(node).opacity);
    if (Number.isFinite(value)) alpha *= value;
    node = node.parentElement;
  }

  return alpha;
}
